// ScanPipeline — UPC scan flow with full data visibility, AI-driven
// canonical proposal, and inline axis editing. Replaces the legacy
// AddDraftSheet's silent autofill with an explicit, auditable surface.
//
// Phases:
//   "scan"    — full-screen BarcodeScanner via portal. Cancel returns
//                to EntryGate.
//   "looking" — animated barcode loader while OFF/USDA/correction
//                lookup runs in parallel.
//   "propose" — no canonical bound: editable popup with raw scan data
//                + AI-suggested name (Haiku via enrichIngredient).
//                User confirms or skips.
//   "review"  — chip strip with inline editors for canonical / brand /
//                category / size, plus a raw-data accordion so the
//                user can see exactly what came back from each source.
//
// Token policy: lib/tokens (FONT, COLOR, CHIP_TONES) only — no mcm
// tokens (per feedback_no_mcm_tokens.md). No reuse of additem/ shared
// modules — every picker / chip / surface in this file is bespoke
// because the shared ones are flagged as iOS-broken.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDraftStore } from "./store";
import { useAuth } from "../../../lib/useAuth";
import { useIngredientInfo } from "../../../lib/useIngredientInfo";
import { useBrandNutrition } from "../../../lib/useBrandNutrition";
import BarcodeScanner from "../../../components/BarcodeScanner";
import { lookupBarcode } from "../../../lib/lookupBarcode";
import { findBarcodeCorrection, fetchPopularPackageSizeForUpc } from "../../../lib/barcodeCorrections";
import { mergeBarcodeFields } from "../../../lib/mergeBarcodeFields";
import { canonicalAutofillSync } from "../../../lib/canonicalAutofill";
import { stripFlavors } from "../../../lib/stripFlavors";
import {
  findIngredient, inferCanonicalFromName, HUBS,
  resolveStorageFromHubChain, getCanonicalHome,
} from "../../../data/ingredients";
import { tagHintsToAxes } from "../../../lib/tagHintsToAxes";
import { parsePackageSize } from "../../../lib/canonicalResolver";
import { suggestCanonical } from "../../../lib/suggestCanonical";
import { rememberBarcodeCorrection } from "../../../lib/barcodeCorrections";
import { findKitchenHomeForCanonical } from "../../../lib/findKitchenHome";
import { FRIDGE_TILES } from "../../../lib/fridgeTiles";
import { PANTRY_TILES } from "../../../lib/pantryTiles";
import { FREEZER_TILES } from "../../../lib/freezerTiles";
import { supabase } from "../../../lib/supabase";
import { brandsForCanonical } from "../../../data/brandExpertise";
import { segmentScanText } from "../../../lib/segmentScanText";
import { LookingCard, ProposeCanonicalCard } from "./ScanLookupView";
import { ReviewSurface } from "./ScanReviewSurface";
import { InlineEditor } from "./ScanAxisEditors";

// Match a Haiku-supplied tileLabel ("Dairy & Eggs", "Frozen Veg") to
// the actual tile id in the location's tile vocab. Case-insensitive,
// strips punctuation so "Meat & Poultry" finds "meat_poultry". Returns
// null when no tile matches — the caller falls back to whatever the
// tag-hints / canonical autofill produced.
function matchTileLabelToId(label, location) {
  if (!label || !location) return null;
  const tiles = location === "fridge"  ? FRIDGE_TILES
              : location === "pantry"  ? PANTRY_TILES
              : location === "freezer" ? FREEZER_TILES
              : null;
  if (!tiles) return null;
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const target = norm(label);
  for (const t of tiles) {
    if (norm(t.label) === target) return t.id;
    if (norm(t.id) === target) return t.id;
  }
  // Fuzzy: token overlap. "Dairy" matches "Dairy & Eggs" via shared
  // first token. Keeps Haiku-supplied short labels ("Dairy") binding
  // to verbose tile labels ("Dairy & Eggs") without exact-match
  // requirement.
  const targetTokens = target.split(/\s+/).filter(Boolean);
  for (const t of tiles) {
    const labelTokens = norm(t.label).split(/\s+/).filter(Boolean);
    const overlap = targetTokens.filter((tok) => labelTokens.includes(tok)).length;
    if (overlap > 0 && overlap === targetTokens.length) return t.id;
  }
  return null;
}

export default function ScanPipeline({ store, registerBack, onSwitchPipeline, isAdmin = false }) {
  const draft = useDraftStore(store, (s) => s);
  const { userId } = useAuth();
  const { ensureByBarcode: ensureBrandNutritionByBarcode } = useBrandNutrition();
  const {
    getInfo: getDbIngredientInfo,
    refreshPending,
    refreshDb,
  } = useIngredientInfo();

  // Phase: initial = scan unless a canonical was already seeded.
  const [phase, setPhase] = useState(draft.canonicalId ? "review" : "scan");
  // Latest scan's raw payload — kept on the component so the review
  // surface's "what we found" panel can display it without re-querying.
  const [rawScan, setRawScan] = useState(null);
  // Haiku verify-or-propose result. When the propose phase opens,
  // ProposeCanonicalCard reads this to pre-fill the editable name.
  const [aiSuggestion, setAiSuggestion] = useState(null);
  // Open inline editor: { axis, ... } | null
  const [editor, setEditor] = useState(null);
  const lookupAbortRef = useRef(false);

  useEffect(() => {
    if (!registerBack) return;
    registerBack(() => {
      if (editor)            { setEditor(null);                                       return true; }
      if (phase === "review" || phase === "propose") {
        store.reset(); setRawScan(null); setPhase("scan");
        return true;
      }
      return false;
    });
  }, [phase, editor, registerBack, store]);

  async function handleDetected(upc) {
    setPhase("looking");
    lookupAbortRef.current = false;
    try {
      const [res, correction, popularPkg] = await Promise.all([
        lookupBarcode(upc, { ensureByBarcode: ensureBrandNutritionByBarcode }),
        findBarcodeCorrection(upc).catch((err) => {
          console.warn("[scan] correction read failed:", err?.message || err);
          return null;
        }),
        fetchPopularPackageSizeForUpc(upc).catch((err) => {
          console.warn("[scan] popular pkg size read failed:", err?.message || err);
          return null;
        }),
      ]);
      if (lookupAbortRef.current) return;

      const merged = mergeBarcodeFields({ correction, off: res });
      setRawScan({ upc, off: res, correction, popularPkg, merged });

      store.setMeta({ barcodeUpc: upc });

      // Canonical resolution cascade.
      //
      //   1. Correction tier (household/global memory) wins
      //      unconditionally — the user already taught this UPC.
      //   2. Programmatic inference produces a TENTATIVE match.
      //   3. Haiku verifies. If the programmatic match is specific
      //      enough (the Gjetost rule), Haiku accepts and we bind.
      //      If too generic, Haiku proposes a more specific name
      //      and we route to the propose popup with the suggestion
      //      pre-loaded.
      //   4. No tentative? Haiku proposes from scratch.
      //
      // Step 1.
      let canonicalId = correction?.canonicalId || null;
      let canonicalSource = canonicalId ? "correction" : null;

      // Step 2 — produce tentative for Haiku to verify. Strip
      // flavor / variant tokens off the raw productName FIRST so
      // "Lay's BBQ Potato Chips 8oz" matches `potato_chips` instead
      // of failing the alias lookup with the marketing copy stuck on
      // the front. Same flaw the sake bug surfaced — bare token has
      // to reach the resolver clean or alias-map word-boundary regex
      // never lands a match. Extracted claims ride forward to Haiku
      // so it knows what was peeled off and doesn't accidentally
      // re-attach them in the bound canonical.
      const flavorScanForResolve = merged.productName
        ? stripFlavors(merged.productName, { brand: merged.brand, removeFromName: true })
        : { remainingName: "", claims: [] };
      const cleanedProductName = (flavorScanForResolve.remainingName || merged.productName || "").trim();
      const extractedClaims = flavorScanForResolve.claims || [];
      let tentativeMatch = null;
      if (!canonicalId && cleanedProductName) {
        const inferred = inferCanonicalFromName(cleanedProductName);
        if (inferred) {
          const ing = findIngredient(inferred);
          tentativeMatch = { canonicalId: inferred, name: ing?.name || inferred };
        }
      }

      // Build brand candidates — OFF brand, USDA brand (carried on
      // the correction row when sourceProvenance.brand === "usda"),
      // correction's own brand value. Deduped, non-empty. When more
      // than one survives, Haiku picks the most user-facing brand
      // (e.g. Tine over Norseland Inc.). Single-candidate or no-
      // candidate cases skip the brand decision entirely.
      const brandCandidates = (() => {
        const seen = new Set();
        const out = [];
        const push = (value, source) => {
          const v = String(value || "").trim();
          if (!v) return;
          const k = v.toLowerCase();
          if (seen.has(k)) return;
          seen.add(k);
          out.push({ value: v, source });
        };
        push(res?.brand, "off");
        // USDA brand sneaks in on the correction row when the ingest
        // pipeline merged USDA's brand_owner. Walk source_provenance
        // to label it correctly so Haiku knows which is which.
        const provBrand = correction?.sourceProvenance?.brand;
        if (correction?.brand && provBrand === "usda") push(correction.brand, "usda");
        else if (correction?.brand) push(correction.brand, "correction");
        return out;
      })();

      // Steps 3 + 4 — Haiku. Skipped when correction already won
      // (the user/household has authoritative say) and when there's
      // no signal to send (no productName AND no ingredients).
      const canCallAi = !canonicalId
        && (merged.productName?.trim() || res?.ingredientsText?.trim());
      let aiBrandPick = null;
      if (canCallAi) {
        try {
          const ai = await suggestCanonical({
            // Send the FLAVOR-STRIPPED name so Haiku verifies
            // against the canonical-ready text instead of the raw
            // marketing copy. Raw productName is included as
            // rawProductName so Haiku can still see what was
            // originally on the label if it disagrees with the
            // strip. extractedClaims carries the peeled flavor
            // tokens so Haiku doesn't try to re-attach them when
            // building the suggestion.
            productName:     cleanedProductName || merged.productName,
            rawProductName:  merged.productName,
            extractedClaims,
            brand:           merged.brand,
            brandCandidates,
            categoryHints:   merged.categoryHints,
            ingredientsText: res?.ingredientsText,
            barcodeUpc:      upc,
            tentativeMatch,
            // Schema vocab — Haiku picks hub/tile slugs FROM these
            // lists when they fit, and proposes new ones (in newHubs)
            // when nothing matches. Keeps the synthetic-canonical
            // landings consistent with the bundled registry.
            registeredHubs: HUBS.map((h) => ({
              id: h.id, name: h.name,
              category: h.category || null,
              parentHub: h.parentHub || null,
            })),
            registeredTiles: {
              fridge:  FRIDGE_TILES.map((t)  => ({ id: t.id, label: t.label })),
              pantry:  PANTRY_TILES.map((t)  => ({ id: t.id, label: t.label })),
              freezer: FREEZER_TILES.map((t) => ({ id: t.id, label: t.label })),
            },
          });
          if (lookupAbortRef.current) return;
          if (ai?.brand?.name) aiBrandPick = ai.brand;
          if (ai?.decision === "accept" && ai.canonicalId) {
            canonicalId = ai.canonicalId;
            canonicalSource = "ai-verified";
          } else if (ai?.decision === "propose" && ai.suggestion) {
            // Stash the AI suggestion; the propose phase reads it.
            // Haiku's brand pick (if any) lives on the suggestion as
            // well — pre-merged so the popup can surface it.
            setAiSuggestion({
              ...ai.suggestion,
              brand: ai.suggestion.brand || aiBrandPick,
            });
            // Pre-fill placement so the propose card / review chips
            // show Haiku's Where + Stored-in + State immediately,
            // without waiting on the user to confirm the canonical.
            // Each setter is gated on correction-tier-not-already-set
            // so the household memory still wins over fresh AI.
            const sug = ai.suggestion;
            if (sug.location && !correction?.location) {
              store.setAxis("location", sug.location, "ai-verified");
            }
            if (sug.tileLabel) {
              const loc = sug.location || correction?.location;
              const tileId = matchTileLabelToId(sug.tileLabel, loc);
              if (tileId && !correction?.tileId) {
                store.setAxis("tileId", tileId, "ai-verified");
              }
            }
            if (sug.state && !correction?.state) {
              store.setAxis("state", sug.state, "ai-verified");
            }
            // Flavor / variant — Haiku's structured `variant` is the
            // product line under brand (e.g. "Honey Nut" for Cheerios);
            // `claims` is the catch-all for demoted tokens. Write
            // both. Variant pins to the structured slot; claims land
            // in attributes.claims at commit and round-trip on re-scan.
            const variantValue = sug.variant
              || (Array.isArray(sug.claims) && sug.claims.length > 0 ? sug.claims[0] : null);
            if (variantValue) {
              store.setAxis("variant", variantValue, "ai-verified");
            }
            if (Array.isArray(sug.claims) && sug.claims.length > 0) {
              store.setMeta({ claims: sug.claims });
            }
          }
        } catch (err) {
          console.warn("[scan] AI verify-or-propose failed:", err?.message || err);
          // Fall back to the tentative match if Haiku errored — better
          // a generic bind than nothing.
          if (tentativeMatch) {
            canonicalId = tentativeMatch.canonicalId;
            canonicalSource = "name-infer";
          }
        }
      }

      if (canonicalId) {
        const ing = findIngredient(canonicalId);
        store.setCanonical(
          canonicalId,
          ing?.name || canonicalId,
          ing?.emoji || null,
          canonicalSource,
        );
        // Resolve dbInfo BEFORE the call. canonicalAutofillSync's
        // signature is `(canonicalId, { dbInfo })` — passing the
        // bare hook function (`{ getInfo }`) silently drops on the
        // floor of the destructure and synthetic canonicals lose
        // their entire enrichment. This was the Gochugaru bug:
        // approved + enriched DB-resident canonicals had storage
        // metadata in info.storage that scan never read because
        // dbInfo never got resolved at the call site.
        const dbInfoForFill = getDbIngredientInfo?.(canonicalId) || null;
        const fill = canonicalAutofillSync(canonicalId, { dbInfo: dbInfoForFill });
        console.info("[scan][autofill] canonicalAutofillSync →", {
          canonicalId,
          dbInfo: dbInfoForFill ? Object.keys(dbInfoForFill).slice(0, 8) : null,
          fillTileId: fill?.tileId || null,
          fillLocation: fill?.location || null,
          fillSource: fill?.source || null,
        });
        if (fill) store.applyAutofill({ ...fill, source: canonicalSource });
      }

      // Brand — prefer Haiku's pick when one was returned (it
      // resolves OFF/USDA conflicts like "Tine vs Norseland Inc"
      // by picking the user-recognizable on-package brand). Fall
      // back to merged.brand otherwise (FIELD_PRIORITIES table).
      const brandValue = aiBrandPick?.name || merged.brand;
      if (brandValue) {
        const src = aiBrandPick
          ? "ai-verified"
          : (correction?.brand ? "correction" : "off");
        store.setAxis("brand", brandValue, src);
      }

      // Variant segmentation — OFF productName mixes brand + variant
      // + adjectives into one marketing string ("Pepsi Wild Cherry 12
      // fl oz"). After canonical + brand are bound, run the segmenter
      // against the canonical's brand catalog (BRAND_EXPERTISE +
      // info.brands.common) to pull a structured variant out. Tier
      // order:
      //   1. correction.variant — taught for THIS upc, wins.
      //   2. Haiku's structured sug.variant — already wired above
      //      inside the propose branch.
      //   3. Segmentation of productName against brand's variants[].
      // Only fires when no variant is set yet — read via
      // store.getState() because the `draft` closure is a stale
      // snapshot from the last render and won't see the setAxis
      // calls Haiku may have made earlier in this same function.
      if (correction?.variant && !store.getState().variant) {
        store.setAxis("variant", correction.variant, "correction");
      }
      if (canonicalId && merged.productName && !store.getState().variant) {
        try {
          const dbInfoForSeg = getDbIngredientInfo?.(canonicalId) || null;
          const brandCatalog = brandsForCanonical(canonicalId, {
            info:  dbInfoForSeg,
            limit: 24,
          });
          // segmentScanText (the shared choke point) instead of the
          // bare catalog segmenter: when the brand catalog doesn't
          // list this variant ("Pepsi" with no "Wild Cherry" entry —
          // the common case for Tier-5 derived brands), the registry-
          // anchored phrase fallback still extracts it instead of
          // dropping it on the floor. Leftover marketing tokens land
          // as claims when Haiku didn't already provide any.
          const seg = segmentScanText(merged.productName, {
            brand: store.getState().brand || merged.brand || null,
            brandCatalog,
            canonicalId,
          });
          if (seg?.variant) {
            store.setAxis("variant", seg.variant, "off-segment");
          }
          const haveClaims = Array.isArray(store.getState().claims)
            && store.getState().claims.length > 0;
          if (!haveClaims && Array.isArray(seg?.claims) && seg.claims.length > 0) {
            store.setMeta({ claims: seg.claims });
          }
        } catch (err) {
          console.warn("[scan] variant segmentation failed:", err?.message || err);
        }
      }

      // Placement — UPC correction tier wins first (taught for THIS
      // upc, never override).
      if (correction?.tileId)   store.setAxis("tileId",   correction.tileId,   "correction");
      if (correction?.location) store.setAxis("location", correction.location, "correction");
      if (correction?.state)    store.setAxis("state",    correction.state,    "correction");
      if (correction?.cut)      store.setAxis("cut",      correction.cut,      "correction");
      if (correction?.typeId)   store.setAxis("typeId",   correction.typeId,   "correction");

      // Kitchen-home tier — when this household already keeps the
      // canonical somewhere, that placement is the strongest signal
      // we have. Read AFTER the correction tier so a UPC-specific
      // teaching wins, but BEFORE OFF tag-hints / canonical metadata
      // since those are upstream defaults that the user's actual
      // kitchen has already overruled by placing the canonical.
      // Async — adds one round-trip but doesn't block anything else
      // up the chain, and the alternative (showing wrong placement
      // chips on every re-scan) is the regression the user flagged.
      if (canonicalId && !lookupAbortRef.current) {
        try {
          const home = await findKitchenHomeForCanonical(canonicalId);
          if (home && !lookupAbortRef.current) {
            if (home.location && !correction?.location) {
              store.setAxis("location", home.location, "kitchen-home");
            }
            if (home.tileId && !correction?.tileId) {
              store.setAxis("tileId", home.tileId, "kitchen-home");
            }
          }
        } catch (err) {
          console.warn("[scan] kitchen-home lookup failed:", err?.message || err);
        }
      }

      if (Array.isArray(merged.categoryHints) && merged.categoryHints.length > 0) {
        const hints = tagHintsToAxes(merged.categoryHints);
        if (hints && !draft.tileId   && hints.tileId)   store.setAxis("tileId",   hints.tileId,   "tag-hints");
        if (hints && !draft.location && hints.location) store.setAxis("location", hints.location, "tag-hints");
        if (hints && !draft.category && hints.category) store.setAxis("category", hints.category, "tag-hints");
      }

      // Final placement fallback — canonical's bundled hub chain.
      // Without this, scans that land on a known canonical but have
      // sparse external signal (no UPC correction, no kitchen-home,
      // no OFF category hints, no AI tile) commit with tileId=null
      // and the kitchen card renders the row in misc despite the
      // canonical's hub clearly pinning a tile (gochugaru →
      // spice_hub → spices_dried_herbs).
      //
      // Reads via getCanonicalHome (4-tier cascade) with a
      // resolveStorageFromHubChain fallback so older bundled rows
      // without a parentId (gochugaru, paprika, oats) still climb
      // through inferHubFromInfo.
      if (canonicalId) {
        const dbInfoLocal = getDbIngredientInfo?.(canonicalId) || null;
        const home = getCanonicalHome(canonicalId, dbInfoLocal)
          || resolveStorageFromHubChain(canonicalId);
        if (home) {
          if (!draft.location && home.location) {
            store.setAxis("location", home.location, "canonical-info");
          }
          if (!draft.tileId) {
            const tile = home.tile;
            const loc  = draft.location || home.location || null;
            let tileId = null;
            if (typeof tile === "string") tileId = tile;
            else if (tile && typeof tile === "object" && loc) tileId = tile[loc];
            else if (tile && typeof tile === "object") tileId = Object.values(tile).find(Boolean) || null;
            if (tileId) store.setAxis("tileId", tileId, "canonical-info");
          }
        }
      }

      // Haiku placement is applied inside the AI try block above —
      // location / tileLabel / state come on the suggestion object
      // and bind to the store the moment they arrive.

      // Package size
      const pkgConsensus = popularPkg?.amount && popularPkg?.unit
        ? { amount: popularPkg.amount, unit: popularPkg.unit, source: "correction" } : null;
      const pkgOff = merged.packageSizeAmount && merged.packageSizeUnit
        ? { amount: merged.packageSizeAmount, unit: merged.packageSizeUnit, source: "off" } : null;
      const pkg = pkgConsensus || pkgOff;
      if (pkg) {
        store.setAxis("packageAmount", pkg.amount, pkg.source);
        store.setAxis("packageUnit",   pkg.unit,   pkg.source);
      } else if (res?.quantity) {
        const parsed = parsePackageSize(res.quantity);
        if (parsed?.amount && parsed?.unit) {
          store.setAxis("packageAmount", parsed.amount, "off");
          store.setAxis("packageUnit",   parsed.unit,   "off");
        }
      }

      // Claims hygiene — strip any claim token that matches the
      // resolved variant. stripFlavors and the variant segmenter both
      // see the same productName, so "Honey BBQ" can land as BOTH a
      // structured variant AND a claim token. Variant wins; the claim
      // duplicate gets dropped so the row doesn't carry the same
      // string twice (once on attributes.variant, once in claims[]).
      const resolvedVariant = store.getState().variant;
      const rawClaims = Array.isArray(merged.claims) ? merged.claims : null;
      const finalClaims = (rawClaims && resolvedVariant)
        ? rawClaims.filter((c) =>
            String(c || "").trim().toLowerCase()
              !== String(resolvedVariant).trim().toLowerCase()
          )
        : rawClaims;

      store.setMeta({
        nutrition:       res?.nutrition || correction?.nutrition || null,
        ingredientsText: res?.ingredientsText || null,
        claims:          finalClaims,
        barcodeMetadata: correction?.productMetadata || res?.metadata || null,
      });

      // No canonical bound? Route to the AI proposal popup.
      // Otherwise straight to review.
      setPhase(canonicalId ? "review" : "propose");
    } catch (err) {
      console.error("[scan] lookup failed:", err);
      if (!lookupAbortRef.current) {
        setRawScan((prev) => ({ ...(prev || { upc }), error: err?.message || String(err) }));
        setPhase("review");
      }
    }
  }

  async function handleProposalConfirm(canonicalIdSlug, displayName) {
    // Bind the canonical client-side so the autofill cascade runs.
    store.setCanonical(canonicalIdSlug, displayName, null, "user");
    const dbInfoForFill = getDbIngredientInfo?.(canonicalIdSlug) || null;
    const fill = canonicalAutofillSync(canonicalIdSlug, { dbInfo: dbInfoForFill });
    if (fill) store.applyAutofill({ ...fill, source: "autofill" });

    // REFRESH THE REGISTRY — the freshly-minted synthetic just landed
    // in pending_ingredient_info (and possibly auto-promoted to
    // ingredient_info if the user is admin). Without an explicit
    // refresh, the local dbMap doesn't know about the slug yet, which
    // means:
    //   • findIngredient(slug) returns null → MCMItemCard / pantry
    //     row reads the row with canonical_id="mac_and_cheese" but
    //     can't resolve it → canonical chip shows blank.
    //   • inferCanonicalFromName won't catch the new slug on the
    //     next scan.
    // Both refreshes are idempotent and re-register the alias map.
    //
    // AWAITED before the review surface mounts — the old fire-and-
    // forget version raced the render and the review screen's
    // canonical chip showed blank until something else re-rendered
    // (verified race, 2026-06 scan audit). Two quick single-row
    // selects; the review phase is worth the beat.
    await Promise.all([
      refreshPending?.(),
      refreshDb?.(),
    ]).catch((err) => console.warn("[scan] post-mint registry refresh failed:", err?.message || err));
    setPhase("review");

    // STOP THE RE-FIRE LOOP — write a UPC correction immediately so
    // the next scan of this barcode resolves via the correction tier
    // and skips the suggestCanonical Haiku call entirely. Previously
    // the correction was only written at Place commit, so rescanning
    // the same item before placing kept re-firing Haiku and re-
    // enriching the same canonical.
    const upc = draft.barcodeUpc || rawScan?.upc;
    if (upc && userId) {
      // Pass variant + brand + name + package size so the FIRST
      // scan persists the full identity the segmenter already
      // extracted. Pre-fix this call sent only canonical_id, which
      // was why "Red's Chicken Sausage Eggwich" landed with
      // variant=null on the global correction row even though the
      // resolveBrandAndVariant pass had already extracted "Chicken
      // Sausage" into the draft.
      // Dump the full raw source payloads into product_metadata so
      // every field OFF/USDA/Haiku gave us — even ones we don't
      // map to first-class columns yet — survives. Searchable via
      // jsonb ops, recoverable by future features that learn to
      // care about (e.g.) ingredients_text, additives_tags,
      // nutriscore_grade, packaging materials, language tags. Was
      // being thrown on the floor before; the eggwich variant
      // regression was a symptom of this broader pattern.
      const rawOff    = rawScan?.off || null;
      const rawHaiku  = aiSuggestion || null;
      const rawMerged = rawScan?.merged || null;
      const productMetadata = {};
      if (rawOff)    productMetadata.off    = rawOff;
      if (rawHaiku)  productMetadata.haiku  = rawHaiku;
      if (rawMerged) productMetadata.merged = rawMerged;
      // Stamp when the raw dump landed so a later admin / migration
      // can re-process old corrections without re-scanning.
      productMetadata.captured_at = new Date().toISOString();

      // AWAITED — the whole point of this write is the re-fire stop:
      // the next scan of this UPC must resolve via the correction
      // tier instead of re-paying Haiku. Fire-and-forget left a race
      // where a quick rescan beat the write and re-fired the AI
      // (verified, 2026-06 scan audit). Failure still degrades soft.
      await rememberBarcodeCorrection({
        userId, isAdmin,
        barcodeUpc:         upc,
        canonicalId:        canonicalIdSlug,
        brand:              draft.brand || null,
        variant:            draft.variant || null,
        name:               draft.name || null,
        packageSizeAmount:  Number.isFinite(draft.max) ? draft.max : null,
        packageSizeUnit:    draft.unit || null,
        productMetadata,
      }).catch((err) => console.warn("[scan] correction write failed:", err?.message || err));
    }

    // Persist the full hub story onto the synthetic canonical's
    // ingredient_info row:
    //   • info.hub        — direct parent slug (last in chain)
    //   • info.hubChain   — full umbrella chain outer→inner
    //   • info.newHubs    — Haiku-proposed hubs that aren't in the
    //                       bundled HUBS list yet (admin-review queue)
    // Fire-and-forget; failure here just means the canonical lives
    // free-floating until an admin assigns a hub manually.
    const parentHub = aiSuggestion?.parentHub;
    const hubChain  = aiSuggestion?.hubChain;
    const newHubs   = aiSuggestion?.newHubs;
    if (parentHub || (Array.isArray(hubChain) && hubChain.length > 0) || (Array.isArray(newHubs) && newHubs.length > 0)) {
      // Raw ingredient_info read — SANCTIONED here despite the
      // CLAUDE.md forbidden-pattern grep: this is a write-path
      // read-modify-write on the row's own JSONB. Routing it through
      // the resolver would merge bundled/overlay values into `info`
      // and bake them back into the DB on update.
      supabase
        .from("ingredient_info")
        .select("ingredient_id, info")
        .eq("ingredient_id", canonicalIdSlug)
        .maybeSingle()
        .then(({ data }) => {
          if (!data) return;
          const nextInfo = { ...(data.info || {}) };
          if (parentHub)                              nextInfo.hub = parentHub;
          if (Array.isArray(hubChain) && hubChain.length > 0) nextInfo.hubChain = hubChain;
          if (Array.isArray(newHubs)  && newHubs.length  > 0) nextInfo.newHubs  = newHubs;
          return supabase
            .from("ingredient_info")
            .update({ info: nextInfo })
            .eq("ingredient_id", canonicalIdSlug);
        })
        .catch((err) => console.warn("[scan] hub metadata upsert failed:", err?.message || err));
    }
  }

  function handleProposalSkip(typedName) {
    // Preserve whatever the user typed before bailing on the propose
    // card. If they typed "Acme Pickled Onions" and then changed
    // their mind, we still want the next user (or the same user re-
    // scanning later) to see that text pre-filled instead of starting
    // from zero. Family-tier write — `name` lands on user_scan_corrections
    // alongside the UPC; no canonical_id is set, so this signals
    // "household saw this UPC and remembered a label, but never bound
    // it." Aggregating WHERE canonical_id IS NULL across the table
    // surfaces dead-end UPCs naturally without a separate observation
    // table.
    const trimmed = String(typedName || "").trim();
    const upc = rawScan?.upc;
    if (upc && userId && trimmed.length >= 2) {
      rememberBarcodeCorrection({
        userId, isAdmin,
        barcodeUpc: upc,
        name: trimmed,
      }).catch((err) => console.warn("[scan] skip-name correction failed:", err?.message || err));
    }
    setAiSuggestion(null);
    setPhase("review");
  }

  function rescan() {
    store.reset();
    setRawScan(null);
    setAiSuggestion(null);
    setPhase("scan");
  }

  return (
    <>
      {phase === "scan" && createPortal(
        <div style={portalShellStyle} onClick={(e) => e.stopPropagation()}>
          <BarcodeScanner
            mode="single"
            onDetected={handleDetected}
            onCancel={() => { lookupAbortRef.current = true; onSwitchPipeline?.("entry"); }}
          />
        </div>,
        document.body,
      )}

      {phase === "looking" && <LookingCard upc={draft.barcodeUpc} />}

      {phase === "propose" && rawScan && (
        <ProposeCanonicalCard
          rawScan={rawScan}
          aiSuggestion={aiSuggestion}
          userId={userId}
          onConfirm={handleProposalConfirm}
          onSkip={handleProposalSkip}
          onRescan={rescan}
          onPhoto={() => onSwitchPipeline?.("photo")}
        />
      )}

      {phase === "review" && (
        <ReviewSurface
          draft={draft}
          rawScan={rawScan}
          dbInfo={draft.canonicalId ? (getDbIngredientInfo?.(draft.canonicalId) || null) : null}
          onPlace={() => store._onPlace?.()}
          onPhoto={() => onSwitchPipeline?.("photo")}
          onWizard={() => onSwitchPipeline?.("wizard")}
          onRescan={rescan}
          onOpenEditor={(key) => setEditor({ axis: key })}
          onSetSize={(amount, unit) => {
            store.setAxis("packageAmount", amount, "user");
            store.setAxis("packageUnit",   unit,   "user");
          }}
          onSetBrand={(brand) => {
            store.setAxis("brand", brand, "user");
          }}
          onSetVariant={(variant) => {
            // Empty-string sentinel = user explicitly dismissed the
            // picker ("none of these"). Stored as null on the row but
            // the "user" override flag keeps the picker from reopening
            // on this draft.
            const value = variant || null;
            store.setAxis("variant", value, "user");
            // Same claims-hygiene as the scan-time variant resolution:
            // drop any claim token equal to the chosen variant so the
            // row doesn't carry it on both axes.
            if (value) {
              const lower = String(value).trim().toLowerCase();
              const current = store.getState().claims;
              if (Array.isArray(current) && current.length > 0) {
                const filtered = current.filter(
                  (c) => String(c || "").trim().toLowerCase() !== lower,
                );
                if (filtered.length !== current.length) {
                  store.setMeta({ claims: filtered });
                }
              }
            }
          }}
        />
      )}

      {editor && (
        <InlineEditor
          editor={editor}
          draft={draft}
          store={store}
          onClose={() => setEditor(null)}
        />
      )}
    </>
  );
}

// ── Styles ──────────────────────────────────────────────────────────

const portalShellStyle = {
  position: "fixed", inset: 0,
  zIndex: 1000,
  background: "#000",
};
